const hasOwn = (value, key) => Object.prototype.hasOwnProperty.call(value || {}, key);
const consumedEmbeddedPresentationStateRows = new WeakSet();

export const mergeStudyInfoForActiveStudy = (
  previous,
  incoming,
  activeStudyInstanceUID,
  { preserveExistingFields = [] } = {},
) => {
  const current = previous?.studyInstanceUID === activeStudyInstanceUID ? previous : null;
  if (incoming?.studyInstanceUID !== activeStudyInstanceUID) return current;
  const merged = { ...(current || {}), ...incoming };
  preserveExistingFields.forEach((field) => {
    const value = current?.[field];
    if (value !== undefined && value !== null && value !== '') merged[field] = value;
  });
  return merged;
};

export const mergePolledSeriesRows = (previousRows = [], polledRows = []) => {
  const previousByUid = new Map(
    previousRows.map((row) => [row.seriesInstanceUID, row]),
  );
  return polledRows.map((row) => {
    const previous = previousByUid.get(row.seriesInstanceUID);
    const carryConsumption = (next) => {
      if (previous && consumedEmbeddedPresentationStateRows.has(previous)) {
        consumedEmbeddedPresentationStateRows.add(next);
      }
      return next;
    };
    if (!previous || hasOwn(row, 'presentationState') || !hasOwn(previous, 'presentationState')) {
      return carryConsumption(row);
    }
    return carryConsumption({ ...row, presentationState: previous.presentationState });
  });
};

export const loadSeriesPresentationState = (series, fetchPresentationState) => {
  if (series && series.presentationState != null && !consumedEmbeddedPresentationStateRows.has(series)) {
    consumedEmbeddedPresentationStateRows.add(series);
    return Promise.resolve(series.presentationState);
  }
  return fetchPresentationState(series.studyInstanceUID, series.seriesInstanceUID);
};

export const loadStudyOpenMetadata = async (
  studyInstanceUID,
  { getBundle, getSeries, getPriors },
) => {
  try {
    const bundle = await getBundle(studyInstanceUID, { priorLimit: 20 });
    return {
      source: 'bundle',
      schemaVersion: bundle.schemaVersion,
      study: bundle.study,
      series: bundle.series,
      priors: bundle.priors,
      metadata: bundle.metadata,
    };
  } catch (bundleError) {
    const status = bundleError?.response?.status;
    const compatibilityFailure = bundleError?.message === 'Unsupported study-open bundle schema';
    if (![404, 413, 503].includes(status) && !compatibilityFailure) {
      throw bundleError;
    }
    const [seriesResult, priorsResult] = await Promise.allSettled([
      getSeries(studyInstanceUID, { skipSearch: status === 503 }),
      getPriors(studyInstanceUID, { limit: 20 }),
    ]);
    if (seriesResult.status === 'rejected') {
      throw seriesResult.reason || bundleError;
    }
    return {
      source: 'legacy',
      study: null,
      series: seriesResult.value || [],
      priors: priorsResult.status === 'fulfilled' ? (priorsResult.value || []) : [],
      metadata: { seriesProjectionConsistent: false },
      ...(priorsResult.status === 'rejected' ? { priorsError: priorsResult.reason } : {}),
    };
  }
};
